<script setup lang="ts">
import { useElementSize } from "@vueuse/core"

import externalBadge from "~/assets/icons/badge/external.svg"
import agentXs from "~/assets/icons/crystalball/crystalball-xs.svg"
import { StaticFile } from "~/classes/file/StaticFile"
import { StaticSearch } from "~/classes/search/StaticSearch"

// TODO: allow selecting the items in the chat, versus just clicking to navigate to them

defineProps({
  request: null,
  response: null
})

const { navigateToSearch, navigateToFile } = useNavigation()
const container = useTemplateRef("container")
const { width } = useElementSize(container)
const search = new StaticSearch(__props.request.search)

const loading = computed(() => !__props.response)
const error = computed(() => __props.response?.error)

const results = computed(() => {
  if (!__props.response?.files)
    return []
  return __props.response.files.map(file => new StaticFile(file))
})

const goToSearch = async () => {
  const search = new StaticSearch(__props.response?.search ?? __props.request.search)
  await navigateToSearch(search, false)
}

const goToFile = async (file: StaticFile) => {
  await navigateToFile(file)
}
</script>

<template>
  <div ref="container" class="search">
    <div
      v-if="!loading"
      class="results"
      :style="{
        gap: `${px(width / 27)} 5%`,
      }"
    >
      <div
        v-for="file in results.slice(0, 7)"
        :key="file.id"
        class="file initial"
        :style="{
          width: px(width / 4.5),
          height: px(width / 4.5),
        }"
        @click="goToFile(file)"
      >
        <file-thumbnail
          :file
          :container-width="width / 4.5"
          :container-height="width / 4.5"
          shadowed
          hide-stale
          class="file-thumbnail"
        />
      </div>
      <div
        v-if="results.length > 7"
        class="rest group"
        @click="goToSearch()"
      >
        <div
          v-for="file in results.slice(7).reverse()"
          :key="file.id"
          class="file"
          :style="{
            width: px(width / 4.5),
            height: px(width / 4.5),
          }"
        >
          <file-thumbnail
            :file
            :container-width="width / 4.5"
            :container-height="width / 4.5"
            shadowed
            hide-stale
            class="file-thumbnail"
          />
        </div>
        <div class="external-badge">
          <external-badge class="external" />
        </div>
      </div>
    </div>
    <dropdown
      anchor-x="left"
      anchor-y="bottom"
      flow-x="right"
      flow-y="down"
    >
      <template #default="{ toggle, active }">
        <div
          class="label"
          :class="{ active }"
          @click="toggle()"
        >
          <interface-spinner
            v-if="loading"
            dots
            class="loading status"
          />
          <agent-xs v-else class="agent status" />
          <icon-action
            size="xs"
            type="chevron-down"
            class="chevron"
          />
          <div v-if="loading">
            Searching for {{ search.description() }}
          </div>
          <div v-else>
            Searched for {{ search.description() }}
          </div>
        </div>
      </template>
      <template #menu="{ toggle }">
        <dropdown-menu class="search-context-menu">
          <context-menu-target-search :search />
        </dropdown-menu>
      </template>
    </dropdown>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.search
  @apply space-y-2

.results
  @apply grid grid-rows-2 grid-cols-4

.row
  @apply flex items-center gap-[#{$panel-agent-gap-step-item}]
  @apply px-[#{$panel-agent-px-step-item}] py-[3px]
  @apply t-panel-xs

.file
  @apply flex items-center justify-center
  @apply transform transition-all transition-duration-100

.file.initial
  @apply cursor-pointer

.rest
  @apply flex items-center justify-center cursor-pointer
  .file
    @apply absolute
    &:nth-child(1)
      @apply rotate-[2deg] shadow-black/30 shadow shadow-lg
    &:nth-child(2)
      @apply -rotate-[4deg] shadow-black/10 shadow shadow-lg
    &:nth-child(3)
      @apply rotate-[4deg] shadow-black/10 shadow shadow-lg
.status
  @apply flex-none flex items-center

.external-badge
  @apply z-100 text-$global-blue-primary transform transition-all transition-duration-100

.rest:hover
  .external-badge
    @apply scale-133
  .file
    &:nth-child(1)
      @apply rotate-[4deg] shadow-xl shadow-black/60
    &:nth-child(2)
      @apply -rotate-[6deg] shadow-xl shadow-black/20
    &:nth-child(3)
      @apply rotate-[2deg] shadow-xl shadow-black/20

.label
  @apply t-panel-sm inline-flex items-center gap-0.75 text-[#{$global-t-secondary}]
  @apply -mx-1 pl-[4px] pr-[8px] py-[2px] rounded-full
  .status
    @apply block
  .chevron
    @apply hidden
  &:hover, &.active
    @apply bg-$global-steel-secondary text-[#{$global-t-default}] cursor-pointer
    .chevron
      @apply block text-[#{$global-t-secondary}]
    .status
      @apply hidden

.agent
  @apply text-$global-violet-primary

.check
  @apply text-$global-green-primary
.cross
  @apply text-$global-red-primary

.search-context-menu
  @apply mt-1 -ml-1
  @apply min-w-40
</style>
